// useCompareSeo.js
// Injects per-article SEO for a single comparison page: <title>, meta
// description, canonical link, and Article + BreadcrumbList JSON-LD. Mirrors
// the guides' useGuideSeo so the Compare section is self-contained.
//
// Pass the comparison metadata object (from compareData). No-ops if null, so it
// can be called unconditionally to satisfy the hooks rule.

import { useEffect } from 'react';

const SITE = 'https://teetimeorganizer.net';

export function useCompareSeo(cmp) {
  useEffect(() => {
    if (!cmp) return;

    const prevTitle = document.title;
    document.title = cmp.title;

    // Meta description
    let desc = document.querySelector('meta[name="description"]');
    let createdDesc = false;
    if (!desc) {
      desc = document.createElement('meta');
      desc.name = 'description';
      document.head.appendChild(desc);
      createdDesc = true;
    }
    const prevDesc = desc.content;
    desc.content = cmp.metaDescription;

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const prevCanonical = canonical.getAttribute('href');
    const url = `${SITE}/compare/${cmp.slug}`;
    canonical.setAttribute('href', url);

    // Article + BreadcrumbList JSON-LD
    const id = `compare-jsonld-${cmp.slug}`;
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: cmp.title,
        description: cmp.metaDescription,
        datePublished: cmp.datePublished,
        dateModified: cmp.dateModified,
        author: { '@type': 'Organization', name: 'Tee Time Organizer' },
        publisher: { '@type': 'Organization', name: 'Tee Time Organizer' },
        mainEntityOfPage: url,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Compare', item: `${SITE}/compare` },
          { '@type': 'ListItem', position: 3, name: cmp.competitor, item: url },
        ],
      },
    ]);

    return () => {
      document.title = prevTitle;
      if (createdDesc) desc.remove();
      else desc.content = prevDesc;
      if (createdCanonical) canonical.remove();
      else if (prevCanonical) canonical.setAttribute('href', prevCanonical);
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, [cmp]);
}
